// PersonRef — a PERSON mentioned on a surface, as the cross-entity reference it
// is (ADR-036 §6): an `EntityRef` to the person — a plain click peeks them, open
// commits to their page — for a viewer who may open a person at all: the people
// key (`users:manage`) guards the person's SURFACE, never their NAME (every
// member names their teammates by membership — ADR-083 §5; the chains' people
// resolve through the membership-gated lookup, ADR-051). Without the key the
// name stays plain text. The key is NARROWED by the managed roles (ADR-050 §1):
// the server says per person whether this viewer may open them (`can_open` on
// the person lookup's rows and a team's member rows — the detail read's rule),
// and the host passes that word; false = plain text, so a reference never
// opens a surface that would refuse. ONE component for every person mention —
// a team's members, a chain's people — so the rule lives once.
//
// The HOST wires the peek (the `EntityRef` contract): a page passes `openPeek`,
// a sheet FOLLOWS (`followPeek` — the person's peek replaces the record, which
// joins the sheet's way back), a focused flow hosts no peek and passes none —
// the name is then plain text, never a click into nothing. A name in a LIST of
// records reads `quiet` (FRONTEND-PATTERNS §Row interaction — colour follows
// ownership): plain at rest, the link colour and a "›" on hover, the "›" at rest
// on touch, where nothing hovers.

import { EntityRef } from './EntityRef.js';
import { icons } from '../ui/icons.js';
import { useCan } from '../../hooks/useCan.js';

export interface PersonRefProps {
  projectId: string;
  /** The person's id (`users.id`). */
  id: string;
  /** The name as the host resolved it. */
  name: string;
  /** The server's word for THIS viewer (`can_open` on the row the name came from): false = the person's surface
   *  would refuse them (a role outside the viewer's managed roles, a project admin), so the name stays plain text. */
  canOpen: boolean;
  /** The host's peek: a page opens the person's peek, a sheet follows to it; absent — no peek hosted (a focused
   *  flow) — the name stays plain text. */
  onPeek?: () => void;
  /** A name in a list of records: plain at rest, the link colour and a "›" on hover (at rest on touch). */
  quiet?: boolean;
  className?: string;
}

export function PersonRef({ projectId, id, name, canOpen, onPeek, quiet = false, className = '' }: PersonRefProps) {
  const can = useCan(projectId);
  if (!canOpen || !onPeek || !can('users:manage')) return <span className={`truncate ${className}`}>{name}</span>;
  return (
    <EntityRef
      name={name}
      to={`/p/${projectId}/users/${id}`}
      onPeek={onPeek}
      quiet={quiet}
      trailing={
        quiet ? (
          <icons.caretRight size={12} aria-hidden className="hidden shrink-0 rtl:-scale-x-100 group-hover/ref:inline group-focus-visible/ref:inline pointer-coarse:inline" />
        ) : undefined
      }
      className={`truncate ${className}`}
    />
  );
}
